import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/common";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="bg-navy text-ivory hero-grain relative hidden lg:flex flex-col justify-between p-14">
        <Link to="/" className="flex items-center gap-3 relative z-10" data-testid="login-logo">
          <span className="h-10 w-10 bg-gold flex items-center justify-center">
            <Scale className="h-5 w-5 text-navy" />
          </span>
          <span className="font-serif text-xl font-semibold">Wanar & Associates</span>
        </Link>
        <div className="relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-5">Attorney Workspace</p>
          <h1 className="font-serif text-5xl font-semibold leading-tight tracking-tight">
            The practice,<br />at a glance.
          </h1>
          <p className="mt-6 text-ivory/60 max-w-md leading-relaxed">
            Clients, cases, documents, and appointments — one secure workspace, built for the way a boutique firm actually runs.
          </p>
        </div>
        <p className="text-xs text-ivory/40 relative z-10">Confidential. Attorney access only.</p>
      </div>

      <div className="flex items-center justify-center bg-ivory p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <span className="h-10 w-10 bg-navy flex items-center justify-center">
              <Scale className="h-5 w-5 text-ivory" />
            </span>
            <span className="font-serif text-xl font-semibold text-navy">Wanar & Associates</span>
          </div>
          <h2 className="font-serif text-4xl font-semibold text-navy">Attorney Sign In</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-10">Enter your credentials to access the practice dashboard.</p>

          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-8" data-testid="login-form" noValidate>
            <Field label="Email Address" htmlFor="login-email">
              <input id="login-email" type="email" autoComplete="email" data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@firm.com"
                className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm" />
            </Field>
            <Field label="Password" htmlFor="login-password">
              <input id="login-password" type="password" autoComplete="current-password" data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm" />
            </Field>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full bg-navy text-ivory hover:bg-navy-800 disabled:opacity-50 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm py-4 inline-flex items-center justify-center gap-2">
              <Lock className="h-4 w-4" /> {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <Link to="/" data-testid="login-back-to-site" className="block mt-8 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-gold-700 transition-colors">
            ← Back to public website
          </Link>
        </div>
      </div>
    </div>
  );
}
